import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PaginationBar from "../components/PaginationBar";
import StoreMap from "../components/StoreMap";
import {
  fetchStores,
  selectStores,
  selectStoresCount,
  selectStoresError,
  selectStoresStatus,
} from "../features/stores/storesSlice";
import "./StoresPage.css";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

export default function StoresPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const PAGE_SIZE = 25;

  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(() => {
    const raw = searchParams.get("page");
    const n = Number(raw || 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  });
  const [viewMode, setViewMode] = useState(() => searchParams.get("view") === "map" ? "map" : "list");

  const rows = useSelector(selectStores);
  const totalCount = useSelector(selectStoresCount);
  const loadStatus = useSelector(selectStoresStatus);
  const error = useSelector(selectStoresError);

  const searchTrimmed = useMemo(() => search.trim(), [search]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTrimmed) next.set("search", searchTrimmed);
    if (page && page !== 1) next.set("page", String(page));
    if (viewMode === "map") next.set("view", "map");
    setSearchParams(next, { replace: true });
  }, [searchTrimmed, page, viewMode, setSearchParams]);

  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlPage = (() => {
      const raw = searchParams.get("page");
      const n = Number(raw || 1);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
    })();

    if (urlSearch !== search) setSearch(urlSearch);
    if (urlPage !== page) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // For map view — load all stores without pagination
  useEffect(() => {
    if (viewMode === "map") {
      dispatch(fetchStores({ search: searchTrimmed, page: 1, pageSize: 1000 }));
    } else {
      dispatch(fetchStores({ search: searchTrimmed, page, pageSize: PAGE_SIZE }));
    }
  }, [dispatch, refreshKey, searchTrimmed, page, viewMode]);

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));

  useEffect(() => {
    if (loadStatus === "succeeded" && page > totalPages && viewMode === "list") {
      setPage(totalPages);
    }
  }, [loadStatus, page, totalCount, totalPages, viewMode]);

  const visibleRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const handleStoreClick = (store) => {
    navigate(`/warehouse/stores/${store.id}`, {
      state: { from: location.pathname + location.search },
    });
  };

  return (
    <div className="stores-page">
      <div className="header">
        <div>
          <div className="title">Склады</div>
          <div className="sub-title">
            Список складов/магазинов (данные из <span className="mono">/api/product/stores/</span>).
          </div>
        </div>

        <div className="header-actions">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск: название / адрес…"
            className="search"
          />
          <div className="view-toggle">
            <button
              type="button"
              className={`btn ${viewMode === "list" ? "btn-active" : ""}`}
              onClick={() => setViewMode("list")}
            >
              Список
            </button>
            <button
              type="button"
              className={`btn ${viewMode === "map" ? "btn-active" : ""}`}
              onClick={() => setViewMode("map")}
            >
              Карта
            </button>
          </div>
          <button type="button" onClick={() => setRefreshKey((v) => v + 1)} className="btn">
            Обновить
          </button>
        </div>
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div className="error">{error}</div>}

      <div className="body">
        {viewMode === "map" ? (
          <StoreMap
            stores={visibleRows}
            height="calc(100vh - 160px)"
            onStoreClick={handleStoreClick}
          />
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th">ID</th>
                    <th className="th">Название</th>
                    <th className="th">Адрес</th>
                    <th className="th">Коорд.</th>
                    <th className="th">Активен</th>
                    <th className="th">Только склад</th>
                    <th className="th">Создан</th>
                    <th className="th">Обновлен</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 && loadStatus === "succeeded" ? (
                    <tr>
                      <td colSpan={8} className="td-muted">
                        Ничего не найдено
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map((r) => (
                      <tr
                        key={r.id}
                        className="tr tr-clickable"
                        title="Нажмите чтобы посмотреть детали"
                        onClick={() => handleStoreClick(r)}
                      >
                        <td className="td-mono">{r.id}</td>
                        <td className="td">{r.name || "—"}</td>
                        <td className="td">{r.address || "—"}</td>
                        <td className="td-mono td-coords">
                          {r.latitude != null && r.longitude != null
                            ? `${r.latitude.toFixed(4)}, ${r.longitude.toFixed(4)}`
                            : <span className="td-muted-inline">—</span>}
                        </td>
                        <td className="td">
                          <span className={`badge ${r.is_active ? "badge-active" : "badge-inactive"}`}>
                            {r.is_active ? "Да" : "Нет"}
                          </span>
                        </td>
                        <td className="td">{r.is_only_warehouse ? "Да" : "Нет"}</td>
                        <td className="td">{formatDateTime(r.created_at)}</td>
                        <td className="td">{formatDateTime(r.update_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalCount > 0 && viewMode === "list" && (
              <PaginationBar
                page={page}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                itemsOnPage={visibleRows.length}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}